import type { Activity } from '../types';

import {
  isActivityToday,
  isActivityOverdue,
  isActivityTomorrow,
  isActivityThisWeek,
  isActivityNextWeek,
} from './activityUtils';

export type ActivityFilter = 'all' | 'to-do' | 'overdue' | 'today' | 'tomorrow' | 'this-week' | 'next-week' | 'done';

export const ACTIVITY_FILTER_VALUES: readonly ActivityFilter[] = [
  'all',
  'to-do',
  'overdue',
  'today',
  'tomorrow',
  'this-week',
  'next-week',
  'done',
] as const;

export const getFilterLabel = (filter: ActivityFilter): string => {
  switch (filter) {
    case 'all':
      return 'All';
    case 'to-do':
      return 'To-do';
    case 'overdue':
      return 'Overdue';
    case 'today':
      return 'Today';
    case 'tomorrow':
      return 'Tomorrow';
    case 'this-week':
      return 'This week';
    case 'next-week':
      return 'Next week';
    case 'done':
      return 'Done';
    default:
      return 'All';
  }
};

export const getFilterDropdownItems = (currentFilter: ActivityFilter, onFilterChange: (filter: ActivityFilter) => void) => {
  return [
    {
      id: 'all',
      label: 'All',
      selected: currentFilter === 'all',
      onClick: () => onFilterChange('all'),
    },
    {
      id: 'to-do',
      label: 'To-do',
      selected: currentFilter === 'to-do',
      onClick: () => onFilterChange('to-do'),
    },
    {
      id: 'overdue',
      label: 'Overdue',
      selected: currentFilter === 'overdue',
      onClick: () => onFilterChange('overdue'),
    },
    {
      id: 'today',
      label: 'Today',
      selected: currentFilter === 'today',
      onClick: () => onFilterChange('today'),
    },
    {
      id: 'tomorrow',
      label: 'Tomorrow',
      selected: currentFilter === 'tomorrow',
      onClick: () => onFilterChange('tomorrow'),
    },
    {
      id: 'this-week',
      label: 'This week',
      selected: currentFilter === 'this-week',
      onClick: () => onFilterChange('this-week'),
    },
    {
      id: 'next-week',
      label: 'Next week',
      selected: currentFilter === 'next-week',
      onClick: () => onFilterChange('next-week'),
    },
    {
      id: 'done',
      label: 'Done',
      selected: currentFilter === 'done',
      onClick: () => onFilterChange('done'),
    },
  ];
};

export const filterActivities = (activities: Activity[], filter: ActivityFilter): Activity[] => {
  switch (filter) {
    case 'all':
      return activities;
    case 'to-do':
      return activities.filter((a) => !a.completed);
    case 'done':
      return activities.filter((a) => a.completed);
    case 'overdue':
      return activities.filter((a) => !a.completed && isActivityOverdue(a));
    case 'today':
      // Today includes both activities due today AND overdue activities
      return activities.filter((a) => !a.completed && (isActivityToday(a) || isActivityOverdue(a)));
    case 'tomorrow':
      return activities.filter((a) => !a.completed && isActivityTomorrow(a));
    case 'this-week':
      return activities.filter((a) => !a.completed && isActivityThisWeek(a));
    case 'next-week':
      return activities.filter((a) => !a.completed && isActivityNextWeek(a));
    default:
      return activities;
  }
};
